/**
 * @Copyright 2014-2019 DataVoke, Inc. All rights reserved.
 */


'use strict';

import {Dictionary} from "../utils/Dictionary";
import {PropertyEntity} from "../entities/property";
import {categories} from './categories';
import {recordTypes} from './recordTypes';
import {createEntityLinker, noop} from "../utils/tools";
import {EntityStates, EntityTypes} from "../utils/dvtypes";

/**
 * @callback MergePropertyEntityCallback
 * @param {PropertyEntity} propertyEntity - The Property Entity that was merged
 * @param {string} actionString - The action that was taken with the merged entity
 */

class PropertyCache {
    constructor () {
        /**
         * Dictionary object for all property entities
         * @type {Dictionary}
         */
        this.allProperties = new Dictionary();
    }

    /**
     * Merge new or updated property entities with the property cache
     * @param {Array<Object>} entities - Array of entity objects that will become PropertyEntity objects
     * @param {MergePropertyEntityCallback} [newCallback = noop]
     * @param {MergePropertyEntityCallback} [updatedCallback = noop]
     */
    mergeCache (entities, newCallback = noop, updatedCallback = noop) {
        if (!Array.isArray(entities)) {
            return;
        }

        entities.forEach(
            /**
             * Loop through all raw JSON property entity objects passed to us
             * @param {Object} entity
             */
            (entity) => {
                let property = new PropertyEntity(entity),
                    cachedProperty = this.allProperties.getVal(property.PropertyID);

                if (!cachedProperty) {
                    // update the state to unmodified - we are getting this from the service, after all...
                    property.setState(EntityStates.Unmodified);

                    // this is a new property, add it to the cache
                    this.allProperties.add(property.PropertyID, property, property.UID);

                    // Update parent Category childProperties...KB
                    let parentCat = categories.allCategories.getVal({ UID: property.CategoryUID });
                    if (parentCat) {
                        if (!parentCat.clientProperties.childProperties) {
                            parentCat.clientProperties.childProperties = [];
                        }

                        // add the property to the parent category
                        parentCat.clientProperties.childProperties.push(property);

                        // ensure that the property has a reference to its parent category
                        property.clientProperties.Parent = parentCat;
                    }
                    else {
                        console.error(`Missing parent category [${property.CategoryUID}] from property [${property.UID}]`);
                    }

                    newCallback(property, 'added');
                }
                else {
                    if (property.State === EntityStates.Deleted) {
                        let parentCat = categories.allCategories.getVal(createEntityLinker(EntityTypes.Category, property.CategoryUID));
                        if (parentCat) {
                            let children = parentCat.clientProperties.childProperties,
                                prop = children.find(p => p.PropertyID === property.PropertyID),
                                index = children.indexOf(prop);
                            if (prop) {
                                children.splice(index, 1);
                            }
                        }

                        // remove the delete entity
                        this.allProperties.remove(property.PropertyID);

                        updatedCallback(property, 'removed');
                    }
                    else {
                        // update the existing property
                        cachedProperty.Definition = property.Definition;
                        cachedProperty.Name = property.Name;

                        updatedCallback(property, 'updated');
                    }
                }
            });
    }

    /**
     * Get all properties for the specified record type
     * @param {string} rtUID - UID string for the record type to get properties for
     * @returns {Array<PropertyEntity>}
     */
    getAllPropertiesForRecordType (rtUID) {
        /**
         * Get the array of category entities for the specified record type ID
         * @type {Array}
         */
        let catsForRT = categories.getAllCategoriesForRecordType(rtUID);

        return this.allProperties
            .filter(prop => catsForRT
                .some(c => c.UID === prop.CategoryUID)).Values;
    }

    /**
     * Get the property entity of the record type indicated by the specified UID string
     * @param {string} rtUID - The UID string of the record type entity being interrogated
     * @returns {PropertyEntity|Null} - The property entity of the record code property of the record type
     */
    getRecordCodePropertyForRecordType (rtUID) {
        return this.allProperties.getVal(recordTypes.allRecordTypes.getVal({ UID: rtUID }).Definition.CodePropertyID);
    }

    /**
     * Create a new "property" as a placeholder for one that is missing.
     * @param {string} recordTypeUID - The record type UID to lookup for the category.
     * @param {string} propertyUID - The UID of the missing property (from a linker that referenced it?)
     * @returns {PropertyEntity} - a new "property" entity object
     */
    newPropertyPlaceholder (recordTypeUID, propertyUID) {
        // get the category for the record type specified.
        let cat = categories.allCategories.Values.find(c => c.RecordTypeUID === recordTypeUID);

        if (!cat) {
            throw new Error(`Unable to get category for property [${propertyUID}] from record type [${recordTypeUID}].`);
        }

        /**
         * Variable for the newly created property entity
         * @type {PropertyEntity|null}
         */
        let prop = null,
            /**
             * Defaults to make the new entity match the missing one
             * @type {Object}
             */
            propDefaults = {
                UID: propertyUID,
                Name: '_MISSING_PROPERTY_',
                CategoryUID: cat.UID,
                Definition: {
                    Name: '[MISSING PROPERTY]'
                }
            };

        this.mergeCache([propDefaults], (property) => {
            prop = property;
        });

        return prop;
    }
}

export const properties = new PropertyCache();
